/**
 * Pure rules for the operations screen, kept out of the components so they are
 * testable without a DOM.
 *
 * The screen answers one question in three views - "is anything stuck" - and the
 * rules below are all about not crying wolf. An ops screen that shows red on a
 * night when nothing is wrong stops being read, and the night something IS wrong
 * is the night it needed to be.
 */
import type {
  DeadLetteredEvent,
  RedriveOutcome,
  RedriveResult,
  ReconciliationRun,
  WebhookDeadLetter,
} from '../../services/ops.service';

export type ProofTone = 'ok' | 'warn' | 'info';

/** There is no bulk re-drive: every row is named, and the server caps the call. */
export const MAX_REDRIVE_PER_CALL = 25;

/**
 * What one night's cash proof means, in a sentence a person can act on.
 *
 * A baseline run is not a pass and not a failure: it is the first night for an
 * account, where there is nothing yet to compare against.
 */
export function describeProof(run: ReconciliationRun): { tone: ProofTone; text: string } {
  if (run.is_baseline || run.status === 'baseline') {
    return { tone: 'info', text: 'First night for this account — nothing to compare yet' };
  }
  if (run.status === 'drift') {
    return { tone: 'warn', text: `The bank and our ledger disagree by ${run.difference}` };
  }
  const loose = run.credits_unexplained + run.debits_unexplained;
  if (loose > 0) {
    return {
      tone: 'warn',
      text: `Balances agree, but ${loose} movement${loose === 1 ? '' : 's'} nothing explains`,
    };
  }
  if (run.unseen_count > 0) {
    return {
      tone: 'warn',
      text: `${run.unseen_count} transaction${run.unseen_count === 1 ? '' : 's'} at the bank we have not imported`,
    };
  }
  return { tone: 'ok', text: 'The bank and our ledger agree' };
}

/** Only a run with something to look at. Used for the "N nights need attention" count. */
export function needsAttention(run: ReconciliationRun): boolean {
  return describeProof(run).tone === 'warn';
}

/**
 * Whether re-driving this row can possibly help.
 *
 * A dead letter from a DELIVERY failure can. One the receiver examined and
 * refused cannot: re-driving it just repeats the refusal, and doing so hides a
 * business problem behind a queue that looks like it is being worked.
 */
export function canRedrive(event: DeadLetteredEvent): boolean {
  return !wasRefused(event);
}

export function wasRefused(event: DeadLetteredEvent): boolean {
  const error = (event.last_error ?? '').toLowerCase();
  // A 4xx from the receiver is a judgement about the content. A 5xx, a timeout
  // or a connection error is the wire.
  return /\b4\d\d\b/.test(error) || error.includes('rejected') || error.includes('refused');
}

export function whyCannotRedrive(event: DeadLetteredEvent): string | null {
  if (canRedrive(event)) return null;
  return 'The other system read this and turned it down. Sending it again repeats the '
    + 'same answer — find out why it was refused first.';
}

/**
 * What to do about a dead-lettered bank notification.
 *
 * Not a re-drive, and not by accident: `fn_guard_bank_webhook_event` refuses any
 * update to a closed row, and it is right to. A webhook is a NOTIFICATION that
 * something happened at the bank, not the thing itself — replaying one we
 * already failed to apply eight times re-applies stale facts. The recovery goes
 * back to the source instead.
 */
export function webhookRecovery(event: WebhookDeadLetter): string {
  return `Re-sync ${event.resource_type} ${event.resource_id} from ${event.provider}. `
    + 'The daily sync re-reads the account from the bank and imports what is really '
    + 'there, which is what this notification was only reporting.';
}

/** Oldest first: a queue is worked from the end that has waited longest. */
export function byAgeOldestFirst<T extends { received_at?: string; dead_lettered_at?: string }>(
  rows: T[],
): T[] {
  const at = (r: T): string => r.dead_lettered_at ?? r.received_at ?? '';
  return [...rows].sort((a, b) => at(a).localeCompare(at(b)));
}

/**
 * What the re-drive actually did, said so the operator does not click again.
 *
 * The server answers per row and never fails the whole call — five events after an
 * outage must not be lost because one had already gone out. Until 2026-09-24 the
 * screen read a `skipped` field the server does not send, so `undefined > 0` was
 * false and a partial result was reported as a clean success: "3 event(s) queued"
 * with no mention of the other two. Whoever selected five rows would reasonably
 * select them again.
 *
 * So every row that did NOT go is named by its reason, and a mixed result is a
 * warning rather than a success — none of these outcomes is an error, but all of
 * them mean something other than what was asked for happened.
 */
export function describeRedriveResult(result: RedriveResult): {
  tone: 'ok' | 'warn';
  text: string;
} {
  const count = (outcome: RedriveOutcome): number =>
    result.results.filter((r) => r.outcome === outcome).length;

  const sent = `${result.redriven} event${result.redriven === 1 ? '' : 's'} queued for delivery again`;
  const asides: string[] = [];
  const published = count('already_published');
  const revived = count('not_dead_lettered');
  const missing = count('not_found');
  if (published > 0) asides.push(`${published} had already been delivered`);
  if (revived > 0) asides.push(`${revived} was no longer abandoned — someone got there first`);
  if (missing > 0) asides.push(`${missing} no longer exists`);

  return asides.length === 0
    ? { tone: 'ok', text: `${sent}.` }
    : { tone: 'warn', text: `${sent}. Of the rest, ${asides.join('; ')}.` };
}

/** A re-drive is a money action: it needs a real reason, not a character count. */
export function isRedriveReady(selected: string[], reason: string, mfaCode: string): boolean {
  return (
    selected.length > 0
    && selected.length <= MAX_REDRIVE_PER_CALL
    && reason.trim().length >= 10
    && mfaCode.trim().length >= 6
  );
}
